import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import { Col, InputNumber, Modal, Row } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
// Constants
// API
import { onPushOptionClicked } from "api/pricerApi";
// Helpers
import { toast } from "helpers/generalHelper";
// Styles
import "./PushOption.scss";
import { GlobalContext } from "contexts/GlobalContext";
import { ACTION_BUTTONS_NAME } from "constants/generalActionButtons";
import CompanyTraders from "components/EditTradeDetails/CompanysTrader";
import AddCompanyForm from "../AddCompanyForm";
import AgreedDeltaInputs from "../AgreedDeltaInputs";
import { EDIT_TRADE_DETAILS_LABELS } from "constants/auditTrail";
import {
  AddButton,
  PrimaryButton,
} from "components/Common/CustomButtons/CustomButtons";
import {
  agreedDeltasValidForSubmit,
  applyAgreedDeltaReadingAt,
  isLiveTeamRunOption,
  setAgreedDeltaField,
  toggleAgreedDeltaUnitAt,
  withAgreedDeltasSubmitPayload,
} from "helpers/TeamRun/agreedDelta";

const LABEL_NAME = {
  SIMILAR_OPTION: { label: "Similar options" },
  PUSH_OPTION_LABEL: { label: "Push Option" },
};

const PushOption = (props) => {
  const { summaryId, onClose, tradingCompanies: propsTradingCompanies, quantity: propsQuantity, data, productId, isOpen } = props;
  const [refetchCompanies, setRefetchCompanies] = useState(false);
  const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [similarOptionsOpen, setSimilarOptionsOpen] = useState(true);
  const [tradingCompanies, setTradingCompanies] = useState(
    propsTradingCompanies
  );
  const [agreedDeltas, setAgreedDeltas] = useState(data?.agreed_deltas ?? []);
  const [quantity, setQuantity] = useState({
    bid_quantity: propsQuantity?.bid_quantity,
    ask_quantity: propsQuantity?.ask_quantity,
  });
  const debounceTimerRef = useRef(null);
  
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  // Update quantity state when props.quantity changes
  useEffect(() => {
    setQuantity({
      bid_quantity: propsQuantity?.bid_quantity,
      ask_quantity: propsQuantity?.ask_quantity,
    });
  }, [propsQuantity]);

  useEffect(() => {
    setAgreedDeltas(data?.agreed_deltas ?? []);
  }, [data?.agreed_deltas]);

  const handleAgreedDeltaChange = (index, field, value) => {
    setAgreedDeltas((prev) => setAgreedDeltaField(prev, index, field, value));
  };

  const handleAgreedDeltaUnitToggle = (index) => {
    setAgreedDeltas((prev) => toggleAgreedDeltaUnitAt(prev, index));
  };

  const handleAgreedDeltaSuggestion = (index, reading) => {
    setAgreedDeltas((prev) => applyAgreedDeltaReadingAt(prev, index, reading));
  };

  const optionLive = data?.live;
  const showAgreedDeltas = !isLiveTeamRunOption(optionLive);
  const deltasValid = agreedDeltasValidForSubmit(optionLive, agreedDeltas);

  const sideAccentClass = (itemKey) =>
    itemKey?.startsWith("bid")
      ? "trader-input__container--bid"
      : "trader-input__container--ask";

  const handleTraderCompanyChange = (index, e) => {
    const updatedTraderCompanies = [...tradingCompanies];
    updatedTraderCompanies[index].value = e;
    setTradingCompanies(updatedTraderCompanies);
  };

  // Extract quantity key from item.key (e.g., "bid_quantity" from "bid_trading_company_id")
  const getQuantityKey = (itemKey) => {
    if (itemKey?.startsWith("bid")) return "bid_quantity";
    if (itemKey?.startsWith("ask")) return "ask_quantity";
    return null;
  };

  // Debounced quantity update handler
  const handleQuantityChange = useCallback((itemKey, value) => {
    const quantityKey = getQuantityKey(itemKey);
    if (!quantityKey) return;

    // Update quantity state immediately for UI responsiveness
    setQuantity((prevQuantity) => ({
      ...prevQuantity,
      [quantityKey]: value,
    }));

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer (500ms delay)
    debounceTimerRef.current = setTimeout(() => {
      // Debounced update - you can add any side effects here if needed
      // For now, the state is already updated above
    }, 500);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  function onClickHandle() {
    if (tradingCompanies.map((item) => item.value).includes(""))
      return toast("Please add a valid trader company name", "warning");

    if (!deltasValid) return;

    setIsLoading(true);
    const extractTraders = {
      ask_trading_company_id: tradingCompanies.find(
        (item) => item.key === "ask_trading_company_id"
      )?.value,
      bid_trading_company_id: tradingCompanies.find(
        (item) => item.key === "bid_trading_company_id"
      )?.value,
    };
    

    onPushOptionClicked(
      withAgreedDeltasSubmitPayload(
        {
          summary_id: summaryId,
          trading_companies: extractTraders,
          same_team_run_row_id: data?.same_team_run_row_id,
          bid_quantity: quantity.bid_quantity,
          ask_quantity: quantity.ask_quantity,
        },
        optionLive,
        agreedDeltas
      )
    )
      .then(() => {
        toast("Pushed Successfully", "success");
        setIsLoading(false);
        onClose(false);
      })
      .then(() => {
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function onAddCompany() {
    setIsAddCompanyOpen(true);
  }

  return (
    <>
      <Modal
        getContainer={"#content-root"}
        title={ACTION_BUTTONS_NAME.PUSH_OPTION.label}
        width={"50vw"}
        onCancel={() => onClose(false)}
        open={isOpen}
        footer={[
          <AddButton
            key={EDIT_TRADE_DETAILS_LABELS.TRADING_COMPANY}
            onClick={() => {
              onAddCompany();
            }}
          >
            {EDIT_TRADE_DETAILS_LABELS.TRADING_COMPANY}
          </AddButton>,
          <PrimaryButton
            loading={isLoading}
            disabled={!deltasValid}
            onClick={onClickHandle}
          >
            {LABEL_NAME.PUSH_OPTION_LABEL.label}
          </PrimaryButton>,
        ]}
        className={`push-option__container ${
          dark_Theme ? "ant-modal-dark" : "ant-modal-light"
        }`}
      >
        {data != null && (
          <div className="push-option__details">
            <div className="align-label-data__container">
              <h6 className={dark_Theme ? "label-light" : "label-dark"}>
                {data.label}
              </h6>
              <span
                className="push-option__value"
                dangerouslySetInnerHTML={{ __html: data.value ?? "" }}
              />
            </div>
            {data.similar_options?.length > 0 && (
              <div className="push-option__similar">
                <button
                  type="button"
                  className="push-option__similar-toggle"
                  onClick={() => setSimilarOptionsOpen((open) => !open)}
                  aria-expanded={similarOptionsOpen}
                >
                  <h6 className={dark_Theme ? "label-light" : "label-dark"}>
                    {LABEL_NAME.SIMILAR_OPTION.label}
                  </h6>
                  {similarOptionsOpen ? <UpOutlined /> : <DownOutlined />}
                </button>
                {similarOptionsOpen ? (
                  <ul className="push-option__similar-list">
                    {data.similar_options.map((item, idx) => (
                      <li
                        key={idx}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ))}
                  </ul>
                ) : null}
              </div>
            )}
          </div>
        )}
        <div className="push-option__traders">
          {tradingCompanies.map((item, index) => (
            <Row
              key={index}
              className={`trader-input__container ${sideAccentClass(item.key)}`}
            >
              <Col span={8} md={5} lg={8}>
                <h6 className={dark_Theme ? "label-light" : "label-dark"}>
                  {item.label}
                </h6>
              </Col>
              <Col span={5}>
                <InputNumber
                  value={
                    quantity[getQuantityKey(item.key)] ??
                    propsQuantity?.[getQuantityKey(item.key)]
                  }
                  onChange={(value) => handleQuantityChange(item.key, value)}
                  min={0}
                  step={1}
                  precision={0}
                  addonAfter={"kt"}
                />
              </Col>
              <Col span={10} xs={4} lg={10}>
                <CompanyTraders
                  defaultValue={item.key}
                  onChange={(value) => handleTraderCompanyChange(index, value)}
                  placeholder="Select Trader Company"
                  isPopup={true}
                  value={item?.value || ""}
                  setRefetchCompanies={setRefetchCompanies}
                  refetchCompanies={refetchCompanies}
                  productId={productId}
                />
              </Col>
            </Row>
          ))}
        </div>
        {showAgreedDeltas ? (
          <AgreedDeltaInputs
            groups={agreedDeltas}
            onChange={handleAgreedDeltaChange}
            onToggleUnit={handleAgreedDeltaUnitToggle}
            onApplySuggestion={handleAgreedDeltaSuggestion}
            darkTheme={dark_Theme}
            disabled={isLoading}
          />
        ) : null}
      </Modal>
      {isAddCompanyOpen && (
        <AddCompanyForm
          isOpen={isAddCompanyOpen}
          setIsOpen={setIsAddCompanyOpen}
          setRefetchCompanies={setRefetchCompanies}
          productId={productId}
        />
      )}
    </>
  );
};

export default PushOption;
